import { Icon } from "@ugitgud/legos/ui/icon/Icon";
import { ReactNode } from "react";
import { SvgYearInReviewKda } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewKda";
import { SvgYearInReviewKills } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewKills";
import { SvgYearInReviewDeath } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewDeath";
import { SvgYearInReviewAssists } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewAssists";
import { SvgYearInReviewDouble } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewDouble";
import { SvgYearInReviewTriple } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewTriple";
import { SvgYearInReviewQuadra } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewQuadra";
import { SvgYearInReviewPenta } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewPenta";
import { SvgYearInReviewRoles } from "@ugitgud/legos/ui/icon/SVG/year-in-review/YearInReviewRoles";
import { SvgArrowLineLeftBig } from "@ugitgud/legos/ui/icon/SVG/ArrowLineLeftBig";
import { SvgArrowLineRightBig } from "@ugitgud/legos/ui/icon/SVG/ArrowLineRightBig";
import { SvgValMvp as SvgYearInReviewCrown } from "@ugitgud/legos/ui/icon/SVG/ValMvp";
import { useState, TouchEvent } from "react";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import classnames from "classnames";

export interface ChampionDataEntry {
  id: string; // Keep as string for now, but pass the ID explicitly
  stats: ChampionStats;
}
export interface ChampionStats {
  wins: number;
  totalGames: number;
  mostKills: number;
  mostDeaths: number;
  avgKills: number;
  avgDeaths: number;
  avgAssists: number;
  doubleKills: number;
  tripleKills: number;
  quadraKills: number;
  pentakills: number;
}

interface ChampStatsModuleProps {
  // This defines an object where keys are Champion IDs (string) and values are ChampionStats
  championData: ChampionDataEntry[];
}

interface FilterButtonProps {
  isActive: boolean;
  onClick: () => void;
}

const TEXT_LABELS = [
  {
    heading: "YOUR POCKET PICK",
    subheading: "Here’s how you did with the one champion that never lets you down.",
  },
  {
    heading: "The back up",
    subheading: "Your main’s banned or countered. Luckily that’s not all you’ve got up your sleeve. Here’s your #2.",
  },
  {
    heading: "The not so good",
    subheading: "So you aren’t the best at every champion. You’re only human. Let’s embrace how bad you are at this one!",
  },
];

const ONE_TRICK_LABEL = {
  heading: "ONE TRICK GOD",
  subheading: "You only have space for one champion in your heart. Let's see how you did.",
};

const FilterButton: React.FC<FilterButtonProps> = ({ isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={classnames("w-3 h-3 shrink-0 rounded-full transition-colors duration-200 cursor-pointer hover:opacity-80", {
        "bg-[#F4C874]": isActive,
        "bg-[#383864]": !isActive,
      })}
    ></button>
  );
};

const StatCard = ({
  icon,
  text,
  value,
  iconColorClass = "text-[#383864]",
}: {
  icon: ReactNode;
  text: string;
  value: string | number;
  iconColorClass?: string;
}) => {
  return (
    <div className="flex justify-between border-b-[1px] border-[#11112A] w-full py-[13px]">
      <div className="flex justify-start gap-x-[8px]">
        <Icon icon={icon} className={classnames("w-[24px] h-[24px]", iconColorClass)} />
        <div className="text-[#CDDCFE] text-[18px] font-[Barlow] font-medium">{text}</div>
      </div>
      <div className="text-white text-[18px] font-[Barlow] font-bold italic">{value}</div>
    </div>
  );
};

const NavButton = ({ onClick, icon }: { onClick: () => void; icon: ReactNode }) => (
  <button onClick={onClick} className="-mt-[20px] p-2 hover:opacity-70 transition-opacity cursor-pointer shrink-0">
    <div className="w-12 h-12 flex items-center justify-center">
      <Icon icon={icon} className="text-[#F4C874] w-12 h-12" />
    </div>
  </button>
);

export const ChampStatsModule: React.FC<ChampStatsModuleProps> = ({ championData }) => {
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [activeIndex, setActiveIndex] = useState(0);

  const champions = championData;
  const showNavigation = champions.length > 1;

  // Destructure the individual entry object at the active index
  const individualChampionData = champions[activeIndex];

  const ChampionId = individualChampionData.id;
  const ChampionStats = individualChampionData.stats;

  const { getChampionName, getChampionSplashImg, getIndividualChampion } = getRiotAssetsContext();
  const championName = getChampionName(ChampionId);
  const championUrl = getChampionSplashImg(ChampionId);
  const championInfo = getIndividualChampion(ChampionId);
  const title = championInfo?.title;

  const kda =
    ChampionStats.avgDeaths > 0
      ? ((ChampionStats.avgKills + ChampionStats.avgAssists) / ChampionStats.avgDeaths).toFixed(1)
      : (ChampionStats.avgKills + ChampionStats.avgAssists).toFixed(1);

  // --- HANDLER ---
  const handleSwitch = (newIndex: number) => {
    if (newIndex === activeIndex) return;
    setDirection(newIndex > activeIndex ? "right" : "left");
    setActiveIndex(newIndex);
  };
  // wrap around logic
  const handleNext = () => {
    const newIndex = (activeIndex + 1) % champions.length;
    setDirection("right");
    setActiveIndex(newIndex);
  };

  const handlePrev = () => {
    const newIndex = (activeIndex - 1 + champions.length) % champions.length;
    setDirection("left");
    setActiveIndex(newIndex);
  };

  // mobile swipe
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) handleNext();
    if (isRightSwipe) handlePrev();
  };

  const currentLabels = !showNavigation ? ONE_TRICK_LABEL : TEXT_LABELS[activeIndex] || TEXT_LABELS[0];
  const animClass = direction === "right" ? "animate-enter-right" : "animate-enter-left";

  return (
    <div className="flex flex-col gap-[40px] items-center">
      <div className="flex xs:flex-row flex-col  w-full xs:w-[756px]  items-center xs:justify-start gap-6">
        {/* champ image */}
        <div
          key={`img-${ChampionId}`}
          className={classnames(
            "w-[236px] h-[156px] shrink-0 border border-[#D29D40] shadow-[0_4px_2px_0_rgba(0,0,0,0.40)] [background-position:80%_10%] [background-size:200%_auto] bg-no-repeat",
            animClass,
          )}
          style={{
            backgroundImage: `url("${championUrl}")`,
          }}
        />

        <div className="flex flex-col items-center xs:items-start">
          {/* champ headings */}
          <div
            key={`text-${ChampionId}-name`}
            className={classnames(
              "opacity-0 [animation-delay:150ms] text-white font-['Barlow'] text-[13px] xs:text-[20px] font-semibold leading-normal uppercase",
              animClass,
            )}
          >
            {championName}, {title}
          </div>
          <div
            key={`text-${ChampionId}-heading`}
            className={classnames(
              "opacity-0 [animation-delay:150ms] text-[#F4C874] font-['Barlow'] text-[30px] xs:text-[48px] font-semibold leading-normal uppercase",
              animClass,
            )}
          >
            {currentLabels.heading}
          </div>
          <div
            key={`text-${ChampionId}-sub-heading`}
            className={classnames(
              "opacity-0 [animation-delay:150ms] text-[#CDDCFE] font-inter text-[11px] xs:text-[18px] font-normal leading-normal xs:text-left text-center",
              animClass,
            )}
          >
            {currentLabels.subheading}
          </div>
        </div>
      </div>

      {/* --- STATS CONTAINER WITH SIDE BUTTONS --- */}
      <div
        className="flex items-start gap-4 touch-pan-y"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Left Button */}
        <div className="hidden xs:block">
          {showNavigation && <NavButton onClick={handlePrev} icon={<SvgArrowLineLeftBig />} />}
        </div>
        {/* Stats Block */}
        <div
          key={`stats-${ChampionId}`}
          className={classnames("opacity-0 [animation-delay:150ms]", "grid grid-cols-2 gap-4 xs:flex xs:gap-[24px]", animClass)}
        >
          <div className="contents xs:flex xs:flex-col xs:w-[236px]">
            <StatCard icon={<SvgYearInReviewCrown />} text={"Win Rate"} value={ChampionStats.wins + "%"} />
            <StatCard icon={<SvgYearInReviewRoles />} text={"Games"} value={ChampionStats.totalGames} />
            <StatCard icon={<SvgYearInReviewKills />} text={"Most Kills"} value={ChampionStats.mostKills} />
            <StatCard icon={<SvgYearInReviewDeath />} text={"Most Deaths"} value={ChampionStats.mostDeaths} />
          </div>
          <div className="contents xs:flex xs:flex-col xs:w-[236px]">
            <StatCard icon={<SvgYearInReviewKda />} text={"KDA"} value={kda} />
            <StatCard icon={<SvgYearInReviewKills />} text={"Avg. Kills"} value={ChampionStats.avgKills} />
            <StatCard icon={<SvgYearInReviewDeath />} text={"Avg. Deaths"} value={ChampionStats.avgDeaths} />
            <StatCard icon={<SvgYearInReviewAssists />} text={"Avg. Assists"} value={ChampionStats.avgAssists} />
          </div>
          <div className="contents xs:flex xs:flex-col xs:w-[236px]">
            <StatCard icon={<SvgYearInReviewDouble />} text={"Double Kills"} value={ChampionStats.doubleKills} />
            <StatCard
              icon={<SvgYearInReviewTriple />}
              text={"Triple Kills"}
              value={ChampionStats.tripleKills}
              iconColorClass="text-[#59D2D9]"
            />
            <StatCard
              icon={<SvgYearInReviewQuadra />}
              text={"Quadra Kills"}
              value={ChampionStats.quadraKills}
              iconColorClass="text-[#00A7FF]"
            />
            <StatCard
              icon={<SvgYearInReviewPenta />}
              text={"Pentakills"}
              value={ChampionStats.pentakills}
              iconColorClass="text-[#FF9B00]"
            />
          </div>
        </div>

        {/* Right Button */}
        <div className="hidden xs:block">
          {showNavigation && <NavButton onClick={handlePrev} icon={<SvgArrowLineRightBig />} />}
        </div>
      </div>

      {/* Filter Buttons */}
      {showNavigation && (
        <div className="flex justify-center mt-[20px] gap-[18px]">
          {champions.map((_, index) => (
            <div key={index} className="flex flex-col items-center gap-2 cursor-pointer" onClick={() => handleSwitch(index)}>
              <FilterButton isActive={activeIndex === index} onClick={() => handleSwitch(index)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
